import { computed, getCurrentInstance, onUnmounted, watch } from 'vue';
import type { Ref } from 'vue';
import { useStore } from 'vuex';
import { useSidebarIsOpen } from './useSidebarIsOpen';
import { useSidebarStore } from '@/stores/useSidebarStore';
import { RIGHT_SIDEBAR, LEFT_SIDEBAR } from '@/types/ui';
import { useAbility } from '@casl/vue';

type DefaultOpenSidebar = 'left' | 'right';

interface UseSynchronizedSidebarsOptions {
  defaultOpenSidebar?: DefaultOpenSidebar;
  /** List pages: restore/save Scout (right sidebar) open state for the session. */
  persistScoutOpenState?: boolean;
}

interface SynchronizedSidebarsReturn {
  isRightSidebarOpen: Ref<boolean>;
  isLeftSidebarOpen: Ref<boolean>;
  stopSynchronization: () => void;
}

/**
 * Composable for synchronizing states of left and right sidebars.
 * By default, when the right sidebar opens, the left one closes.
 */
export function useSynchronizedSidebars(
  options: UseSynchronizedSidebarsOptions = {},
): SynchronizedSidebarsReturn {
  const { can } = useAbility();
  const vuexStore = useStore();
  const sidebarStore = useSidebarStore();
  const hasAi = can('use', 'Ai');
  const defaultOpenSidebar =
    options.defaultOpenSidebar ?? (hasAi ? 'right' : 'left');
  const openRightByDefault = defaultOpenSidebar === 'right' && hasAi;
  const rightSidebarInitialState = openRightByDefault;
  const leftSidebarInitialState = !openRightByDefault;
  const isRightSidebarOpen = useSidebarIsOpen(
    RIGHT_SIDEBAR,
    rightSidebarInitialState,
  );
  const isLeftSidebarOpen = useSidebarIsOpen(
    LEFT_SIDEBAR,
    leftSidebarInitialState,
  );

  const sessionScope = computed(() => ({
    userId: vuexStore.getters['auth/user']?.id as
      | string
      | number
      | null
      | undefined,
    accountId: vuexStore.getters['auth/accountId'] as number | null | undefined,
  }));

  if (options.persistScoutOpenState) {
    const hydrated = sidebarStore.hydrateScoutOpenFromSession(
      sessionScope.value,
    );
    if (!hydrated) {
      sidebarStore.applyPageDefaultSidebars(defaultOpenSidebar, hasAi);
    }
  } else {
    sidebarStore.applyPageDefaultSidebars(defaultOpenSidebar, hasAi);
  }

  const stopSyncWatcher = watch(isRightSidebarOpen, (val) => {
    isLeftSidebarOpen.value = !val;
  });

  const stopPersistWatcher = options.persistScoutOpenState
    ? watch(isRightSidebarOpen, () => {
        sidebarStore.persistScoutOpenToSession(sessionScope.value);
      })
    : () => {};

  const stopSynchronization = () => {
    stopSyncWatcher();
    stopPersistWatcher();
  };

  if (getCurrentInstance()) {
    onUnmounted(stopSynchronization);
  }

  return {
    isRightSidebarOpen,
    isLeftSidebarOpen,
    stopSynchronization,
  };
}
